<template>
  <v-card
    v-if="loading"
    height="150px"
    class="rounded-lg d-flex justify-center align-center"
    flat
  >
    <div>
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
  </v-card>
  <div v-else>
    <div>
      <v-card class="rounded-lg pa-4" flat>
        <v-card-title class="justify-center">
          <p>{{ $t('organization_billing.plan_drawer.change_plan') }}</p>
        </v-card-title>
        <v-card-text class="text-center grey--text text--darken-3">
          <p>
            {{ $t('organization_billing.plan_drawer.description') }}
          </p>
        </v-card-text>
        <v-divider class="mb-4" />
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-card-text>
            <v-row>
              <v-col
                cols="12"
                md="6"
                class="d-flex justify-center align-center"
              >
                <h3 class="body-1 text-center">
                  {{ $t('organization_billing.create.pricing_model') }}
                </h3>
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="organizationPlanUpdate.pricing_model_id"
                  :items="pricingModels"
                  item-text="display_name"
                  item-value="id"
                  :rules="rules.pricingModelRules"
                  label="Pricing Model"
                  class="rounded-lg"
                  hide-details
                  outlined
                  @change="updatePricingModel"
                >
                </v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                md="4"
                class="d-flex justify-center align-center"
              >
                <h3 class="body-1 text-center">
                  {{ $t('organization_billing.currency') }}
                </h3>
              </v-col>
              <v-col cols="12" md="8">
                <v-autocomplete
                  v-model="organizationPlanUpdate.currency_id"
                  :items="currenciesPlans"
                  item-text="name"
                  item-value="id"
                  :rules="rules.currencyRules"
                  :label="$t('organization_billing.plan_drawer.currency')"
                  hide-details
                  outlined
                  class="rounded-lg"
                  @change="updateCurrency"
                >
                </v-autocomplete>
              </v-col>
            </v-row>
            <v-row v-if="organizationPlanUpdate.currency_id">
              <v-col
                cols="12"
                md="4"
                class="d-flex justify-center align-center"
              >
                <h3 class="body-1 text-center">
                  {{ $t('organization_billing.license') }}
                </h3>
              </v-col>
              <v-col cols="12" md="8">
                <v-autocomplete
                  v-model="organizationPlanUpdate.plan_id"
                  :items="filteredPlans"
                  item-text="display_name"
                  item-value="id"
                  :rules="rules.planRules"
                  :label="$t('organization_billing.plan')"
                  hide-details
                  outlined
                  class="rounded-lg"
                  @change="updatePlan"
                >
                </v-autocomplete>
              </v-col>
            </v-row>
            <!------------------------------------------>
            <!-- SERVICES ------------------------------>
            <!------------------------------------------>
            <div v-if="showServicesAndResume">
              <v-row
                v-if="
                  organizationPlanUpdate.plan_id && planHasCustomAutomation()
                "
              >
                <v-col
                  cols="12"
                  md="4"
                  class="d-flex justify-center align-center"
                >
                  <h3 class="body-1 text-center">
                    {{ $t('organization_billing.create.services') }}
                  </h3>
                </v-col>
                <v-col cols="12" md="8">
                  <v-autocomplete
                    v-model="organizationPlanUpdate.services"
                    :items="filteredServices"
                    item-text="display_name"
                    item-value="id"
                    :rules="rules.servicesRules"
                    multiple
                    chips
                    hide-details
                    outlined
                    class="rounded-lg"
                    return-object
                    @change="updateServices"
                  >
                    <template v-slot:item="{ props, item }">
                      <div>
                        <v-list-item
                          v-bind="props"
                          :disabled="isServiceTypeSelected(item)"
                        >
                          <v-list-item-title>
                            {{ item.display_name }}
                          </v-list-item-title>
                        </v-list-item>
                      </div>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <div
                v-if="
                  organizationPlanUpdate.plan_id && planHasServicesRelated()
                "
              >
                <v-row
                  v-for="service in defaultRelatedServices"
                  :key="service.id"
                  class="mt-2"
                >
                  <v-col
                    cols="12"
                    md="6"
                    class="d-flex justify-center align-center"
                  >
                    <h3 class="body-1 text-center">
                      {{ $t('organization_billing.service') }}
                    </h3>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card
                      class="rounded-md pa-4"
                      flat
                      outlined
                      style="border: 1px solid #9e9e9e;"
                    >
                      <h3 class="body-1">{{ service.display_name }}</h3>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
              <v-row
                v-if="organizationPlanUpdate.plan_id && planHasCustomAmount()"
              >
                <v-col
                  cols="12"
                  md="4"
                  class="d-flex justify-center align-center"
                >
                  <h3 class="body-1 text-center">
                    {{ $t('organization_billing.amount') }}
                  </h3>
                </v-col>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="organizationPlanUpdate.amount"
                    type="number"
                    :placeholder="
                      `${$t(
                        'organization_billing.plan_drawer.amount'
                      )} ${currencySelected}`
                    "
                    :label="currencySelected"
                    :rules="rules.amountRules"
                    outlined
                    hide-details
                    class="rounded-lg"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
              <v-row
                v-if="organizationPlanUpdate.plan_id && planHasCustomFee()"
              >
                <v-col
                  cols="12"
                  md="4"
                  class="d-flex justify-center align-center"
                >
                  <h3 class="body-1 pt-2 text-center">
                    {{ $t('organization_billing.fee_percentage') }} <br />
                    <p class="body-2 text-center">
                      {{ $t('organization_billing.fee_percentage_subtext') }}
                    </p>
                  </h3>
                </v-col>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="organizationPlanUpdate.fee_percentage"
                    type="number"
                    :placeholder="
                      $t('organization_billing.plan_drawer.percentage')
                    "
                    :rules="rules.percentageRules"
                    :label="$t('organization_billing.plan_drawer.percentage')"
                    outlined
                    hide-details
                    class="rounded-lg"
                  >
                  </v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
          <v-card-actions class="pa-4 d-flex justify-end align-end">
            <v-btn
              color="#2661FF"
              min-width="100px"
              @click="update"
              :disabled="!valid"
            >
              <span style="color: #fff;">
                {{ this.$t('general.save') }}
              </span>
            </v-btn>
          </v-card-actions>
        </v-form>
        <PlanResume v-if="organizationPlan.plan_id" />
      </v-card>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import PlanResume from '@/components/Organization/Details/PlanResume'
import plansMixin from '@/mixins/plans'

export default {
  components: { PlanResume },
  mixins: [plansMixin],
  props: {
    organization_id: { type: Number, required: true }
  },
  data() {
    return {
      loading: false,
      successModal: false,
      valid: true,
      currencySelected: null,
      plansCustom: [],
      organizationPlanUpdate: {
        id: null,
        services: [],
        amount: null,
        plan_id: null,
        currency: null,
        currency_id: null,
        fee_percentage: null,
        organization_id: null,
        pricing_model_id: null
      },
      rules: {
        currencyRules: [v => !!v || 'This field is required'],
        planRules: [v => !!v || 'This field is required'],
        servicesRules: [v => !!v || 'This field is required'],
        percentageRules: [
          v => !!v || 'This field is required',
          v => /^\d+(\.\d+)?$/.test(v) || 'Number only',
          v =>
            (v && v.toString().split('.').length < 2) ||
            (v && v.toString().split('.')[1].length <= 2) ||
            'No more than 2 digits after the decimal point',
          v => (v && v >= 0) || 'Min should be above 0%',
          v => (v && v <= 100) || 'Max should not be above 100%'
        ],
        amountRules: [
          v => !!v || 'This field is required',
          v => /^\d+(\.\d+)?$/.test(v) || 'Number only',
          v =>
            (v && v.toString().split('.').length < 2) ||
            (v && v.toString().split('.')[1].length <= 2) ||
            'No more than 2 digits after the decimal point',
          v => (v && v >= 0) || 'Min should be above 0'
        ],
        pricingModelRules: [v => !!v || 'This field is required']
      }
    }
  },
  async created() {
    try {
      this.loading = true
      await this.getPlans()
      await this.getPricingModels()
      this.copyOrganizationPlan()
      this.showOrganizationPlanCurrency()
      this.initializePlansCustom()
    } catch (error) {
      console.log(error)
      this.$noty.error('Error getting data')
    } finally {
      this.loading = false
    }
  },
  computed: {
    ...mapState({
      organizationPlan: ({ organization: { organizationPlan } }) =>
        organizationPlan,
      organizationCurrencyCode: ({ organization: { organization } }) =>
        organization.currency,
      currencies: ({ countries: { currencies } }) => currencies,
      plans: ({ plans: { plans } }) => plans,
      services: ({ plans: { services } }) => services,
      pricingModels: ({ pricingModels: { pricingModels } }) => pricingModels
    }),
    currenciesPlans() {
      /* ALLOWS THE USER TO SELECT BETWEEN THE POSSIBLES CURRENCIES FOR THE PLAN */
      const pricing = this.findPricingModel(
        this.organizationPlanUpdate.pricing_model_id
      )
      if (pricing) {
        const { currencies } = pricing

        return currencies
      }
      return []
    },
    filteredPlans() {
      /* SHOWS THE POSSIBLES PLANS DEPENDING OF THE PREVIOUS SELECTED CURRENCY */
      return this.plans.filter(plan => {
        return (
          (plan.currency_id === this.organizationPlanUpdate.currency_id &&
            plan.pricing_model_id ===
              this.organizationPlanUpdate.pricing_model_id) ||
          !plan.currency_id
        )
      })
    },
    /* SHOWS THE POSSIBLES SERVICES DEPENDING OF THE PREVIOUS SELECTED CURRENCY */
    filteredServices() {
      const { services, organizationPlanUpdate } = this
      return services.filter(service => {
        return service.currency_id === organizationPlanUpdate.currency_id
      })
    },
    /* FINDS THE RELATED SERVICES OF THE SELECTED PLAN */
    defaultRelatedServices() {
      const { plans, organizationPlanUpdate } = this

      const relatedPlanServices = plans.find(
        plan => plan.id === organizationPlanUpdate.plan_id
      ).billingServices

      if (relatedPlanServices) {
        organizationPlanUpdate.services = relatedPlanServices
        return relatedPlanServices
      }

      return null
    },
    showServicesAndResume() {
      return ![this.getPlanIdByName('no_plan')].includes(
        this.organizationPlanUpdate.plan_id
      )
    }
  },
  methods: {
    ...mapActions({
      getPlans: 'plans/getPlans',
      updateOrganizationPlan: 'organization/updateOrganizationPlan',
      getPricingModels: 'pricingModels/getPricingModels'
    }),
    copyOrganizationPlan() {
      if (this.organizationPlan) {
        // Check if organizationPlan is defined and not null
        const {
          id,
          amount,
          plan_id,
          billingServices,
          currency,
          currency_id,
          fee_percentage,
          organization_id
        } = this.organizationPlan

        const { pricing_model_id } = this.plans.find(plan => {
          return plan.id === this.organizationPlan.plan_id
        })

        this.organizationPlanUpdate = {
          id,
          amount,
          plan_id,
          services: billingServices,
          currency,
          currency_id,
          fee_percentage,
          organization_id,
          pricing_model_id
        }
      }
    },
    // Define plans which have custom amount [monthly - lite - essentials - professional]
    initializePlansCustom() {
      this.plansCustom = [
        this.getPlanIdByName('monthly_license_USD'),
        this.getPlanIdByName('monthly_license_BRL'),
        this.getPlanIdByName('monthly_license_MXN'),
        this.getPlanIdByName('monthly_license_PEN'),
        this.getPlanIdByName('monthly_license_COP'),
        this.getPlanIdByName('monthly_license_EUR'),
        this.getPlanIdByName('lite_monthly_license_USD'),
        this.getPlanIdByName('lite_monthly_license_BRL'),
        this.getPlanIdByName('lite_monthly_license_MXN'),
        this.getPlanIdByName('lite_monthly_license_PEN'),
        this.getPlanIdByName('lite_monthly_license_COP'),
        this.getPlanIdByName('lite_monthly_license_EUR'),
        this.getPlanIdByName('essentials_monthly_license_USD'),
        this.getPlanIdByName('essentials_monthly_license_BRL'),
        this.getPlanIdByName('essentials_monthly_license_MXN'),
        this.getPlanIdByName('essentials_monthly_license_PEN'),
        this.getPlanIdByName('essentials_monthly_license_COP'),
        this.getPlanIdByName('essentials_monthly_license_EUR'),
        this.getPlanIdByName('professional_monthly_license_USD'),
        this.getPlanIdByName('professional_monthly_license_BRL'),
        this.getPlanIdByName('professional_monthly_license_MXN'),
        this.getPlanIdByName('professional_monthly_license_PEN'),
        this.getPlanIdByName('professional_monthly_license_COP'),
        this.getPlanIdByName('professional_monthly_license_EUR')
      ]
    },
    showOrganizationPlanCurrency() {
      /* SHOWS THE CURRENCY CODE IN THE LABEL WHEN THE USER HAS TO PROVIDE THE AMOUNT ONLY FOR [monthly_license] WHEN IT INITIALIZE*/
      if (this.organizationPlanUpdate.currency_id) {
        return (this.currencySelected = this.organizationPlanUpdate.currency.code)
      } else {
        return (this.currencySelected = null)
      }
    },
    findPricingModel(pricing_model_id) {
      return this.pricingModels.find(pricing => pricing.id === pricing_model_id)
    },
    updatePricingModel() {
      // Reset organizationPlan values when pricing model changes
      this.organizationPlanUpdate.currency_id = null
      this.organizationPlanUpdate.plan_id = null
      this.organizationPlanUpdate.services = []
      this.organizationPlanUpdate.fee_percentage = null
      this.organizationPlanUpdate.amount = null
      this.organizationPlanUpdate.currency = null
    },
    updateCurrency() {
      // Reset organizationPlan values when currency changes
      this.organizationPlanUpdate.plan_id = null
      this.organizationPlanUpdate.services = []
      this.organizationPlanUpdate.fee_percentage = null
      this.organizationPlanUpdate.amount = null

      // Set currency in the inputs
      const currency = this.getCurrency()
      this.currencySelected = currency ? currency.code : null
    },
    updatePlan() {
      // Reset organizationPlan values when plan changes
      this.organizationPlanUpdate.services = []
      this.organizationPlanUpdate.fee_percentage = null
      this.organizationPlanUpdate.amount = null
    },
    planHasServicesRelated() {
      const plan = this.getPlan()
      return plan.billingServices.length > 0
    },
    planHasCustomAutomation() {
      const plan = this.getPlan()
      const { custom_automation } = plan
      return custom_automation
    },
    planHasCustomAmount() {
      const plan = this.getPlan()
      const { custom_amount } = plan
      return custom_amount // Boolean
    },
    planHasCustomFee() {
      const plan = this.getPlan()
      const { custom_fee } = plan
      return custom_fee // Boolean
    },
    updateServices(selectedServices) {
      const { organizationPlanUpdate } = this

      // Creamos un Set para almacenar los tipos ya vistos
      const seenTypes = new Set()

      // Filtramos los servicios seleccionados
      organizationPlanUpdate.services = selectedServices.filter(service => {
        if (seenTypes.has(service.type)) {
          // Si el tipo ya está en el Set, no incluimos este servicio
          return false
        } else {
          // Si es un nuevo tipo, lo añadimos al Set y mantenemos el servicio
          seenTypes.add(service.type)
          return true
        }
      })
    },
    isServiceTypeSelected(item) {
      return this.organizationPlanUpdate.services.some(
        service => service.type === item.type
      )
    },
    async update() {
      if (this.$refs.form.validate()) {
        const selectedPlan = this.plans.find(
          plan => plan.id === this.organizationPlanUpdate.plan_id
        )

        // SET FEE PERCENTAGE - CUSTOM OR PLAN RELATED
        this.setPercentage(selectedPlan)
        // SET AMOUNT - CUSTOM OR PLAN RELATED
        this.setAmount(selectedPlan)
        // NO PLAN
        this.setNoPlan(selectedPlan)

        const {
          id,
          amount,
          plan_id,
          services,
          currency_id,
          fee_percentage,
          organization_id
        } = this.organizationPlanUpdate

        await this.updateOrganizationPlan({
          id,
          amount,
          plan_id,
          services,
          currency_id,
          fee_percentage,
          organization_id
        })
      }
    },
    validate() {
      this.$refs.form.validate()
    },
    reset() {
      this.$refs.form.reset()
    },
    resetValidation() {
      this.$refs.form.resetValidation()
    },
    getPlan() {
      const plan = this.plans.find(plan => {
        return plan.id === this.organizationPlanUpdate.plan_id
      })
      return plan
    },
    getCurrency() {
      const currency = this.currencies.find(
        curr => curr.id === this.organizationPlanUpdate.currency_id
      )
      return currency
    },
    // UPDATE VALIDATIONS
    setPercentage(selectedPlan) {
      let CUSTOM_PERCENTAGE =
        this.organizationPlanUpdate.fee_percentage && selectedPlan.custom_fee

      console.log({
        CUSTOM_PERCENTAGE,
        selectedPlan,
        fee_percentage: this.organizationPlanUpdate.fee_percentage
      })

      if (CUSTOM_PERCENTAGE) {
        this.organizationPlanUpdate.fee_percentage = this.organizationPlanUpdate.fee_percentage.replace(
          ',',
          '.'
        )
      } else if (selectedPlan) {
        this.organizationPlanUpdate.fee_percentage = selectedPlan.fee_percentage.replace(
          ',',
          '.'
        )
      }
    },
    setAmount(selectedPlan) {
      const RELATED_PLAN_AMOUNT =
        !this.organizationPlanUpdate.amount && selectedPlan
      if (RELATED_PLAN_AMOUNT) {
        this.organizationPlanUpdate.amount = selectedPlan.amount
      }
    },
    setNoPlan(selectedPlan) {
      const VALIDATE_NO_PLAN =
        this.organizationPlanUpdate.plan_id === this.getPlanIdByName('no_plan')
      if (VALIDATE_NO_PLAN) {
        // Relate automation no plan
        this.organizationPlanUpdate.services = []
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
